/**
 * ENS (ENS_ES) Controls CAT Catalog
 * Based on ENS Medidas de Seguridad - Marco Organizativo, Marco de Protección, Marco Operacional
 */

export type EnsControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
export type EnsDomain = 'Marco Organizativo' | 'Marco de Protección' | 'Marco Operacional';

export interface EnsControl {
  standard: string;
  controlCode: string;
  domain: EnsDomain;
  title: { en: string; es: string; ru: string };
  description: { en: string; es: string; ru: string };
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE';
  evidence: { type: string; examples: { en: string[]; es: string[]; ru: string[] } }[];
  vendorImpact: { applies: boolean };
  governanceLinks: { policies: string[]; processes: string[]; roles: string[] };
  crossMapping: { ISO27001: string[]; GDPR: string[]; NIS2: string[] };
  recommendations: {
    CRITICAL?: { en: string; es: string; ru: string };
    MAJOR?: { en: string; es: string; ru: string };
    MODERATE?: { en: string; es: string; ru: string };
  };
}

const ev = (type: string, en: string[], es: string[], ru: string[]) => ({
  type,
  examples: { en, es, ru },
});

const def = (
  code: string,
  domain: EnsDomain,
  title: { en: string; es: string; ru: string },
  desc: { en: string; es: string; ru: string },
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE',
  evidence: EnsControl['evidence'],
  vendor: boolean,
  rec: Partial<EnsControl['recommendations']>,
  cross: Partial<EnsControl['crossMapping']> = {}
): EnsControl => ({
  standard: 'ENS_ES',
  controlCode: `ENS_${code}`,
  domain,
  title,
  description: desc,
  severity,
  evidence,
  vendorImpact: { applies: vendor },
  governanceLinks: { policies: [], processes: [], roles: [] },
  crossMapping: { ISO27001: [], GDPR: [], NIS2: [], ...cross },
  recommendations: rec,
});

// Marco Organizativo
const ENS_MO: EnsControl[] = [
  def('MO_01', 'Marco Organizativo',
    { en: 'Security Policy', es: 'Política de Seguridad', ru: 'Политика безопасности' },
    { en: 'Documented and approved information security policy.', es: 'Política de seguridad documentada y aprobada.', ru: 'Документированная и утверждённая политика ИБ.' },
    'CRITICAL', [ev('policy', ['Security policy'], ['Política de seguridad'], ['Политика безопасности'])],
    false, { CRITICAL: { en: 'Define and approve ENS security policy.', es: 'Definir y aprobar política de seguridad ENS.', ru: 'Определить и утвердить политику безопасности ENS.' } },
    { ISO27001: ['A.5.1'] }),
  def('MO_05', 'Marco Organizativo',
    { en: 'Asset Management', es: 'Gestión de Activos', ru: 'Управление активами' },
    { en: 'Asset inventory maintained and classified.', es: 'Inventario de activos mantenido y clasificado.', ru: 'Реестр активов ведётся и классифицируется.' },
    'CRITICAL', [ev('record', ['Asset inventory'], ['Inventario'], ['Реестр активов'])],
    false, { CRITICAL: { en: 'Maintain and classify asset inventory.', es: 'Mantener y clasificar inventario de activos.', ru: 'Вести и классифицировать реестр активов.' } }),
  def('MO_06', 'Marco Organizativo',
    { en: 'Risk Management', es: 'Gestión de Riesgos', ru: 'Управление рисками' },
    { en: 'Risk management process implemented.', es: 'Proceso de gestión de riesgos implementado.', ru: 'Процесс управления рисками реализован.' },
    'CRITICAL', [ev('procedure', ['Risk methodology'], ['Metodología'], ['Методология'])],
    false, { CRITICAL: { en: 'Implement ENS risk management process.', es: 'Implementar proceso de gestión de riesgos ENS.', ru: 'Внедрить процесс управления рисками ENS.' } }),
  def('MO_08', 'Marco Organizativo',
    { en: 'Supplier Management', es: 'Gestión de Proveedores', ru: 'Управление поставщиками' },
    { en: 'Supplier security requirements defined.', es: 'Requisitos de seguridad de proveedores definidos.', ru: 'Требования безопасности поставщиков определены.' },
    'MAJOR', [ev('policy', ['Supplier policy'], ['Política proveedores'], ['Политика поставщиков'])],
    true, { MAJOR: { en: 'Define supplier security requirements.', es: 'Definir requisitos de seguridad de proveedores.', ru: 'Определить требования безопасности поставщиков.' } }),
  def('MO_20', 'Marco Organizativo',
    { en: 'Incident Management', es: 'Gestión de Incidentes', ru: 'Управление инцидентами' },
    { en: 'Incident management process implemented.', es: 'Proceso de gestión de incidentes implementado.', ru: 'Процесс управления инцидентами реализован.' },
    'CRITICAL', [ev('procedure', ['IR procedures'], ['Procedimientos IR'], ['Процедуры IR'])],
    false, { CRITICAL: { en: 'Implement incident management process.', es: 'Implementar proceso de gestión de incidentes.', ru: 'Внедрить процесс управления инцидентами.' } }),
];

// Marco de Protección
const ENS_MP: EnsControl[] = [
  def('MP_03', 'Marco de Protección',
    { en: 'Access Control', es: 'Control de Acceso', ru: 'Контроль доступа' },
    { en: 'Access control mechanisms implemented.', es: 'Mecanismos de control de acceso implementados.', ru: 'Механизмы контроля доступа реализованы.' },
    'CRITICAL', [ev('procedure', ['Access policy'], ['Política acceso'], ['Политика доступа'])],
    false, { CRITICAL: { en: 'Implement access control per ENS.', es: 'Implementar control de acceso según ENS.', ru: 'Внедрить контроль доступа в соответствии с ENS.' } }),
  def('MP_08', 'Marco de Protección',
    { en: 'Data Encryption', es: 'Cifrado de Datos', ru: 'Шифрование данных' },
    { en: 'Encryption for sensitive data implemented.', es: 'Cifrado para datos sensibles implementado.', ru: 'Шифрование чувствительных данных реализовано.' },
    'MAJOR', [ev('record', ['Encryption config'], ['Config cifrado'], ['Настройки шифрования'])],
    false, { MAJOR: { en: 'Implement encryption for sensitive data.', es: 'Implementar cifrado para datos sensibles.', ru: 'Внедрить шифрование чувствительных данных.' } }),
  def('MP_18', 'Marco de Protección',
    { en: 'Vulnerability Management', es: 'Gestión de Vulnerabilidades', ru: 'Управление уязвимостями' },
    { en: 'Vulnerability scanning and management implemented.', es: 'Escaneo y gestión de vulnerabilidades implementados.', ru: 'Сканирование и управление уязвимостями реализованы.' },
    'MAJOR', [ev('record', ['Scan reports'], ['Informes escaneo'], ['Отчёты сканирования'])],
    false, { MAJOR: { en: 'Implement vulnerability management.', es: 'Implementar gestión de vulnerabilidades.', ru: 'Внедрить управление уязвимостями.' } }),
];

// Marco Operacional
const ENS_OP: EnsControl[] = [
  def('OP_01', 'Marco Operacional',
    { en: 'Business Continuity Plan', es: 'Plan de Continuidad', ru: 'План непрерывности' },
    { en: 'BCP documented and tested.', es: 'BCP documentado y probado.', ru: 'BCP документирован и протестирован.' },
    'CRITICAL', [ev('record', ['BCP document'], ['Documento BCP'], ['Документ BCP'])],
    false, { CRITICAL: { en: 'Document and test BCP.', es: 'Documentar y probar BCP.', ru: 'Документировать и тестировать BCP.' } }),
  def('OP_02', 'Marco Operacional',
    { en: 'Backup Management', es: 'Gestión de Respaldo', ru: 'Управление резервным копированием' },
    { en: 'Backup procedures implemented and tested.', es: 'Procedimientos de respaldo implementados y probados.', ru: 'Процедуры резервирования реализованы и протестированы.' },
    'CRITICAL', [ev('record', ['Backup logs'], ['Registros backup'], ['Журналы резервирования'])],
    false, { CRITICAL: { en: 'Implement and test backup procedures.', es: 'Implementar y probar procedimientos de respaldo.', ru: 'Внедрить и тестировать процедуры резервирования.' } }),
  def('OP_16', 'Marco Operacional',
    { en: 'Security Monitoring', es: 'Monitoreo de Seguridad', ru: 'Мониторинг безопасности' },
    { en: 'Continuous security monitoring implemented.', es: 'Monitoreo continuo de seguridad implementado.', ru: 'Непрерывный мониторинг безопасности реализован.' },
    'MAJOR', [ev('record', ['SIEM/Logs'], ['SIEM/Logs'], ['SIEM/Логи'])],
    false, { MAJOR: { en: 'Implement continuous security monitoring.', es: 'Implementar monitoreo continuo de seguridad.', ru: 'Внедрить непрерывный мониторинг безопасности.' } }),
];

export const ENS_CONTROLS_CATALOG: EnsControl[] = [...ENS_MO, ...ENS_MP, ...ENS_OP];

const DOMAIN_ORDER: EnsDomain[] = ['Marco Organizativo', 'Marco de Protección', 'Marco Operacional'];

export function getEnsControlsByDomain(domain: EnsDomain): EnsControl[] {
  return ENS_CONTROLS_CATALOG.filter((c) => c.domain === domain);
}

export function getEnsControlByCode(code: string): EnsControl | undefined {
  return ENS_CONTROLS_CATALOG.find(
    (c) => c.controlCode === code || c.controlCode === `ENS_${code}`
  );
}

export function getEnsControlDomains(): EnsDomain[] {
  return DOMAIN_ORDER;
}
